/* eslint-disable no-await-in-loop */
/* eslint-disable indent */
import {IMAGE_HEIGHT, IMAGE_SIZE, IMAGE_TYPE, IMAGE_WIDTH, PRODUCT_LISTING_ONDEMANDCOLUMNS, StateActions, UPSELL_DEFAULT_FILTERS} from 'extensions/multi-system/data/constants';
import {eventStateChange} from 'extensions/multi-system/utils/events';
import 'extensions/ui-components/x-loader';
import {RelatedProductListController} from 'extensions/ui-controllers/related-product-list';
import {defineCustomElement, getRuntimeProductPricing} from 'extensions/ui-utils';
import {LitElement, html, nothing} from 'lit';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import stylesheet from './x-step-upsell.scss?as=stylesheet';

const targetIsClickable = target => {
	switch (target?.nodeName?.toLowerCase()) {
		case 'a':
		case 'button':
		case 'input':
		case 'select':
		case 'textarea':
			return true;
		default:
			return false;
	}
};

export class StepUpsell extends LitElement {
	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			loading: {
				state: true,
				type: Boolean
			},
			products: {
				state: true,
				type: Array
			},
			selectedProducts: {
				attribute: 'selected-products',
				type: Array
			},
			selectedRoom: {
				attribute: 'selected-room',
				type: Number
			},
			upsellProducts: {
				attribute: 'upsell-products',
				type: Array
			}
		};
	}

	get filters () {
		return [
			...UPSELL_DEFAULT_FILTERS,
			{
				name: 'imagetypes',
				value: {
					sizes: [
						IMAGE_SIZE,
						'original'
					],
					types: [IMAGE_TYPE]
				}
			}
		];
	}

	constructor () {
		super();

		// Default values

		/**
		 * @type {boolean}
		 */
		this.loading = true;

		/**
		 * @type {import('extensions/ui-controllers/product-list/lib/product-list').MivaProduct[]}
		 */
		this.products = [];

		/**
		 * @type {string[]}
		 */
		this.selectedProducts = [];

		/**
		 * @type {number}
		 */
		this.selectedRoom = 0;

		/**
		 * @type {import('extensions/multi-system/types').UpsellProduct[]}
		 */
		this.upsellProducts = [];
	}

	async getProducts () {
		await this.updateComplete;

		this.loading = true;

		const uniqueProducts = Array.from(new Set(this.selectedProducts));

		/**
		 * @type {string[]}
		 */
		const uniqueRelatedProducts = [];

		for (let uniqueProductCode of uniqueProducts) {
			const relatedProductListController = new RelatedProductListController(null, {
				cache: true,
				count: 10,
				filters: this.filters,
				listCode: 'optional-parts',
				moduleFunctionName: 'Runtime_ProductXRelatedProduct_List_Load_Query',
				onDemandColumns: [
					...PRODUCT_LISTING_ONDEMANDCOLUMNS,
					'category'
				],
				productCode: uniqueProductCode
			});

			// Get related products from API
			const relatedProducts = await relatedProductListController.getProducts(false);

			// Add related product codes to array
			for (let relatedProduct of relatedProducts) {
				const found = uniqueRelatedProducts.find(uniqueRelatedProduct => uniqueRelatedProduct.id === relatedProduct.id);

				if (!found) {
					uniqueRelatedProducts.push(relatedProduct);
				}
			}
		}

		this.products = await getRuntimeProductPricing(uniqueRelatedProducts);

		this.loading = false;
	}

	handleAddProduct (productCode, e) {
		const [target] = e.composedPath();
		if (targetIsClickable(target)) {
			e.preventDefault();
			return false;
		}

		this.dispatchEvent(eventStateChange({
			bubbles: true,
			composed: true,
			detail: {
				action: StateActions.UpsellProductAdd,
				merge: true,
				payload: {
					code: productCode,
					quantity: 1
				}
			}
		}));

		return true;
	}

	handleRemoveProduct (productCode, e) {
		const [target] = e.composedPath();
		if (targetIsClickable(target)) {
			e.preventDefault();
			return false;
		}

		this.dispatchEvent(eventStateChange({
			bubbles: true,
			composed: true,
			detail: {
				action: StateActions.UpsellProductRemove,
				merge: true,
				payload: productCode
			}
		}));

		return true;
	}

	handleProductQuantityChange (e) {
		const {code, quantity} = e.detail;

		this.dispatchEvent(eventStateChange({
			bubbles: true,
			composed: true,
			detail: {
				action: StateActions.UpsellProductQuantityUpdate,
				merge: true,
				payload: {
					code,
					quantity
				}
			}
		}));
	}

	updated (changedProperties) {
		if (changedProperties.has('selectedRoom')) {
			this.getProducts();
		}
	}

	/**
	 * @param {import('extensions/ui-controllers/product-list').MivaProduct} product
	 */
	renderInventoryMessage (product) {
		if (product.inventory_display_html) {
			return html`
				<div slot="inventory">
					${unsafeHTML(product.inventory_display_html)}
				</div>
			`;
		}

		return nothing;
	}

	renderProducts () {
		if (this.loading) {
			return html`<x-loader class="loader" aspect-ratio-desktop="8/2" aspect-ratio-tablet="8/2" aspect-ratio="8/2"></x-loader>`;
		}

		return html`
			<div class="products">
				${this.products?.map(product => {
					const selected = this.upsellProducts?.find(upsellProduct => upsellProduct.code === product.code);

					return html`
						<x-selectable-card ?selected="${selected}" @click="${(e) => selected ? this.handleRemoveProduct(product.code, e) : this.handleAddProduct(product.code, e)}">
							<x-product-card
								class="product"
								@change="${this.handleProductQuantityChange}"
								show-details-link
								?show-quantity="${selected}"
								quantity="${selected?.quantity || 1}"
								base-price="${product.base_price}"
								category="${product.category?.name}"
								formatted-price="${product.formatted_price}"
								formatted-base-price="${product.formatted_base_price}"
								image-height="${IMAGE_HEIGHT}"
								image-url="${product?.imagetypes[IMAGE_TYPE]?.sizes[IMAGE_SIZE]?.url || product?.imagetypes[IMAGE_TYPE]?.sizes?.original?.url}"
								image-width="${IMAGE_WIDTH}"
								price="${product.price}"
								sku="${product.code}"
								url="${product.url}"
								?hide-pricing="${!window.mivaJS?.Customer_Permissions?.WebstorePricing}"
							>
								${this.renderInventoryMessage(product)}
							</x-product-card>
						</x-selectable-card>
					`;
				})}
			</div>
		`;
	}

	render () {
		return html`
			<div class="container">
				<x-section heading="Optional Add-Ons">
					${this.renderProducts()}
				</x-section>
			</div>
		`;
	}
};

defineCustomElement('x-step-upsell', StepUpsell);
